/**
 * Filter Field Definitions
 * All available fields for audience segmentation
 */

import {
  FilterCategoryDefinition,
  FilterFieldDefinition,
  FilterOperator,
  FieldType,
} from "../types/audienceTypes";

// ============================================================================
// Contact Properties
// ============================================================================

const contactFields: FilterFieldDefinition[] = [
  {
    id: "business_role",
    label: "Business Role",
    type: "select",
    category: "contact",
    operators: ["is_any_of", "is_none_of", "is_known", "is_unknown"],
    options: [
      { id: "owner_gm", label: "Owner / GM" },
      { id: "manager", label: "Manager" },
      { id: "technician", label: "Technician" },
      { id: "sales", label: "Sales" },
      { id: "call_center", label: "Call Center" },
      { id: "hr_recruiting", label: "HR / Recruiting" },
      { id: "finance", label: "Finance / Back Office" },
      { id: "marketing", label: "Marketing" },
    ],
    description: "The employee's business role",
  },
  {
    id: "job_title",
    label: "Job Title",
    type: "text",
    category: "contact",
    operators: ["equals", "not_equals", "contains", "starts_with", "is_known", "is_unknown"],
    placeholder: "Enter job title...",
  },
  {
    id: "hire_date",
    label: "Hire Date",
    type: "date",
    category: "contact",
    operators: ["is", "is_before", "is_after", "is_between", "is_more_than_days_ago", "is_less_than_days_ago"],
    description: "When the employee was hired",
  },
  {
    id: "email_domain",
    label: "Email Domain",
    type: "text",
    category: "contact",
    operators: ["equals", "not_equals", "contains", "ends_with"],
    placeholder: "@company.com",
  },
  {
    id: "account_created",
    label: "Account Created",
    type: "date",
    category: "contact",
    operators: ["is", "is_before", "is_after", "is_between", "is_more_than_days_ago"],
    description: "When the user account was created",
  },
  {
    id: "is_active",
    label: "Active Employee",
    type: "boolean",
    category: "contact",
    operators: ["is_true", "is_false"],
    description: "Employee has not been terminated",
  },
];

// ============================================================================
// Company Properties
// ============================================================================

const companyFields: FilterFieldDefinition[] = [
  {
    id: "company_name",
    label: "Company Name",
    type: "text",
    category: "company",
    operators: ["equals", "not_equals", "contains", "starts_with"],
    placeholder: "Enter company name...",
  },
  {
    id: "trade",
    label: "Trade",
    type: "multi_select",
    category: "company",
    operators: ["is_any_of", "is_none_of"],
    options: [
      { id: "electrical", label: "Electrical", icon: "⚡" },
      { id: "hvac", label: "HVAC", icon: "❄️" },
      { id: "plumbing", label: "Plumbing", icon: "🔧" },
      { id: "roofing", label: "Roofing", icon: "🏠" },
    ],
  },
  {
    id: "state",
    label: "State",
    type: "select",
    category: "company",
    operators: ["is_any_of", "is_none_of"],
    options: [
      { id: "AL", label: "Alabama" },
      { id: "AK", label: "Alaska" },
      { id: "AZ", label: "Arizona" },
      { id: "AR", label: "Arkansas" },
      { id: "CA", label: "California" },
      { id: "CO", label: "Colorado" },
      { id: "CT", label: "Connecticut" },
      { id: "DE", label: "Delaware" },
      { id: "FL", label: "Florida" },
      { id: "GA", label: "Georgia" },
      { id: "HI", label: "Hawaii" },
      { id: "ID", label: "Idaho" },
      { id: "IL", label: "Illinois" },
      { id: "IN", label: "Indiana" },
      { id: "IA", label: "Iowa" },
      { id: "KS", label: "Kansas" },
      { id: "KY", label: "Kentucky" },
      { id: "LA", label: "Louisiana" },
      { id: "ME", label: "Maine" },
      { id: "MD", label: "Maryland" },
      { id: "MA", label: "Massachusetts" },
      { id: "MI", label: "Michigan" },
      { id: "MN", label: "Minnesota" },
      { id: "MS", label: "Mississippi" },
      { id: "MO", label: "Missouri" },
      { id: "MT", label: "Montana" },
      { id: "NE", label: "Nebraska" },
      { id: "NV", label: "Nevada" },
      { id: "NH", label: "New Hampshire" },
      { id: "NJ", label: "New Jersey" },
      { id: "NM", label: "New Mexico" },
      { id: "NY", label: "New York" },
      { id: "NC", label: "North Carolina" },
      { id: "ND", label: "North Dakota" },
      { id: "OH", label: "Ohio" },
      { id: "OK", label: "Oklahoma" },
      { id: "OR", label: "Oregon" },
      { id: "PA", label: "Pennsylvania" },
      { id: "RI", label: "Rhode Island" },
      { id: "SC", label: "South Carolina" },
      { id: "SD", label: "South Dakota" },
      { id: "TN", label: "Tennessee" },
      { id: "TX", label: "Texas" },
      { id: "UT", label: "Utah" },
      { id: "VT", label: "Vermont" },
      { id: "VA", label: "Virginia" },
      { id: "WA", label: "Washington" },
      { id: "WV", label: "West Virginia" },
      { id: "WI", label: "Wisconsin" },
      { id: "WY", label: "Wyoming" },
    ],
  },
  {
    id: "city",
    label: "City",
    type: "text",
    category: "company",
    operators: ["equals", "not_equals", "contains"],
    placeholder: "Enter city...",
  },
  {
    id: "zip_code",
    label: "Zip Code",
    type: "text",
    category: "company",
    operators: ["equals", "starts_with", "is_any_of"],
    placeholder: "Enter zip code...",
  },
  {
    id: "employee_count",
    label: "Company Size",
    type: "number",
    category: "company",
    operators: ["equals", "greater_than", "less_than", "between"],
    description: "Number of employees at the company",
  },
  {
    id: "field_service_software",
    label: "Field Service Software",
    type: "select",
    category: "company",
    operators: ["is_any_of", "is_none_of", "is_known", "is_unknown"],
    options: [
      { id: "servicetitan", label: "ServiceTitan" },
      { id: "housecall_pro", label: "Housecall Pro" },
      { id: "jobber", label: "Jobber" },
      { id: "fieldedge", label: "FieldEdge" },
      { id: "service_fusion", label: "Service Fusion" },
      { id: "other", label: "Other" },
    ],
  },
];

// ============================================================================
// Subscription & Access
// ============================================================================

const subscriptionFields: FilterFieldDefinition[] = [
  {
    id: "eagles_access",
    label: "Eagles Access",
    type: "boolean",
    category: "subscription",
    operators: ["is_true", "is_false"],
    description: "Has Eagles subscription",
  },
  {
    id: "lata_access",
    label: "LATA (Total Access)",
    type: "boolean",
    category: "subscription",
    operators: ["is_true", "is_false"],
    description: "Has Total Access subscription",
  },
  {
    id: "certainpath_status",
    label: "CertainPath Member",
    type: "boolean",
    category: "subscription",
    operators: ["is_true", "is_false"],
    description: "Is a CertainPath company",
  },
  {
    id: "subscription_tier",
    label: "Subscription Tier",
    type: "select",
    category: "subscription",
    operators: ["is_any_of", "is_none_of"],
    options: [
      { id: "free", label: "Free" },
      { id: "eagles", label: "Eagles" },
      { id: "lata", label: "Total Access (LATA)" },
    ],
  },
  {
    id: "application_access",
    label: "Application Access",
    type: "multi_select",
    category: "subscription",
    operators: ["is_any_of", "is_none_of"],
    options: [
      { id: "hub", label: "Hub" },
      { id: "learning_center", label: "Learning Center" },
      { id: "stochastic", label: "Stochastic (Direct Mail)" },
      { id: "call_intelligence", label: "Call Intelligence" },
      { id: "scoreboard", label: "Scoreboard" },
    ],
  },
];

// ============================================================================
// Event Engagement
// ============================================================================

const eventFields: FilterFieldDefinition[] = [
  {
    id: "event_attended",
    label: "Attended Event",
    type: "select",
    category: "event",
    operators: ["is_any_of", "is_none_of"],
    options: [], // Dynamic - loaded from API
    description: "Has attended a specific event",
  },
  {
    id: "events_attended_count",
    label: "Events Attended",
    type: "number",
    category: "event",
    operators: ["equals", "greater_than", "less_than", "between"],
    description: "Total number of events attended",
  },
  {
    id: "last_event_date",
    label: "Last Event Attended",
    type: "date",
    category: "event",
    operators: ["is", "is_before", "is_after", "is_more_than_days_ago", "is_less_than_days_ago"],
  },
  {
    id: "event_category",
    label: "Event Category",
    type: "select",
    category: "event",
    operators: ["is_any_of", "is_none_of"],
    options: [], // Dynamic
  },
  {
    id: "registration_status",
    label: "Registration Status",
    type: "select",
    category: "event",
    operators: ["is_any_of", "is_none_of"],
    options: [
      { id: "registered", label: "Registered" },
      { id: "attended", label: "Attended" },
      { id: "no_show", label: "No Show" },
      { id: "cancelled", label: "Cancelled" },
    ],
  },
  {
    id: "used_voucher",
    label: "Used Event Voucher",
    type: "boolean",
    category: "event",
    operators: ["is_true", "is_false"],
  },
  {
    id: "event_favorites_count",
    label: "Favorited Events",
    type: "number",
    category: "event",
    operators: ["equals", "greater_than", "less_than"],
    description: "Number of favorited events",
  },
];

// ============================================================================
// Learning Center
// ============================================================================

const learningFields: FilterFieldDefinition[] = [
  {
    id: "course_enrolled",
    label: "Enrolled in Course",
    type: "select",
    category: "learning",
    operators: ["is_any_of", "is_none_of"],
    options: [], // Dynamic
  },
  {
    id: "course_completed",
    label: "Completed Course",
    type: "select",
    category: "learning",
    operators: ["is_any_of", "is_none_of"],
    options: [], // Dynamic
  },
  {
    id: "course_status",
    label: "Course Status",
    type: "select",
    category: "learning",
    operators: ["is_any_of"],
    options: [
      { id: "enrolled", label: "Enrolled" },
      { id: "in_progress", label: "In Progress" },
      { id: "completed", label: "Completed" },
      { id: "not_started", label: "Not Started" },
    ],
  },
  {
    id: "course_progress",
    label: "Course Progress %",
    type: "number",
    category: "learning",
    operators: ["equals", "greater_than", "less_than", "between"],
    description: "Percentage completion of current course",
  },
  {
    id: "courses_completed_count",
    label: "Courses Completed",
    type: "number",
    category: "learning",
    operators: ["equals", "greater_than", "less_than"],
  },
  {
    id: "badge_earned",
    label: "Earned Badge",
    type: "select",
    category: "learning",
    operators: ["is_any_of", "is_none_of"],
    options: [], // Dynamic
  },
  {
    id: "total_badges",
    label: "Total Badges",
    type: "number",
    category: "learning",
    operators: ["equals", "greater_than", "less_than"],
  },
  {
    id: "total_points",
    label: "Total Points",
    type: "number",
    category: "learning",
    operators: ["equals", "greater_than", "less_than", "between"],
  },
  {
    id: "learning_path_enrolled",
    label: "Enrolled in Path",
    type: "select",
    category: "learning",
    operators: ["is_any_of", "is_none_of"],
    options: [], // Dynamic
  },
  {
    id: "learning_path_completed",
    label: "Completed Path",
    type: "boolean",
    category: "learning",
    operators: ["is_true", "is_false"],
  },
];

// ============================================================================
// Call Intelligence / Performance
// ============================================================================

const performanceFields: FilterFieldDefinition[] = [
  {
    id: "call_score_total",
    label: "Overall Call Score",
    type: "number",
    category: "performance",
    operators: ["equals", "greater_than", "less_than", "between"],
    description: "Total call score (0-100)",
  },
  {
    id: "call_score_opening",
    label: "Opening Score",
    type: "number",
    category: "performance",
    operators: ["greater_than", "less_than", "between"],
  },
  {
    id: "call_score_listening",
    label: "Listening Score",
    type: "number",
    category: "performance",
    operators: ["greater_than", "less_than", "between"],
  },
  {
    id: "manager_attention_flag",
    label: "Flagged for Manager",
    type: "boolean",
    category: "performance",
    operators: ["is_true", "is_false"],
    description: "Needs manager attention",
  },
  {
    id: "call_count",
    label: "Total Calls",
    type: "number",
    category: "performance",
    operators: ["equals", "greater_than", "less_than"],
    description: "Number of recorded calls",
  },
  {
    id: "last_call_date",
    label: "Last Call Date",
    type: "date",
    category: "performance",
    operators: ["is", "is_before", "is_after", "is_more_than_days_ago"],
  },
];

// ============================================================================
// Financial
// ============================================================================

const financialFields: FilterFieldDefinition[] = [
  {
    id: "total_spend",
    label: "Total Spend",
    type: "number",
    category: "financial",
    operators: ["equals", "greater_than", "less_than", "between"],
    description: "Total amount spent on events/courses",
  },
  {
    id: "outstanding_balance",
    label: "Outstanding Balance",
    type: "number",
    category: "financial",
    operators: ["equals", "greater_than", "less_than"],
  },
  {
    id: "invoice_status",
    label: "Invoice Status",
    type: "select",
    category: "financial",
    operators: ["is_any_of", "is_none_of"],
    options: [
      { id: "paid", label: "Paid" },
      { id: "pending", label: "Pending" },
      { id: "overdue", label: "Overdue" },
    ],
  },
  {
    id: "last_purchase_date",
    label: "Last Purchase",
    type: "date",
    category: "financial",
    operators: ["is", "is_before", "is_after", "is_more_than_days_ago"],
  },
  {
    id: "used_discount",
    label: "Used Discount Code",
    type: "boolean",
    category: "financial",
    operators: ["is_true", "is_false"],
  },
];

// ============================================================================
// Email History
// ============================================================================

const emailHistoryFields: FilterFieldDefinition[] = [
  {
    id: "received_campaign",
    label: "Received Campaign",
    type: "select",
    category: "email_history",
    operators: ["is_any_of", "is_none_of"],
    options: [], // Dynamic
  },
  {
    id: "email_opened",
    label: "Opened Email",
    type: "boolean",
    category: "email_history",
    operators: ["is_true", "is_false"],
    description: "Has opened any marketing email",
  },
  {
    id: "email_clicked",
    label: "Clicked Email Link",
    type: "boolean",
    category: "email_history",
    operators: ["is_true", "is_false"],
  },
  {
    id: "email_bounced",
    label: "Email Bounced",
    type: "boolean",
    category: "email_history",
    operators: ["is_true", "is_false"],
  },
  {
    id: "marked_spam",
    label: "Marked as Spam",
    type: "boolean",
    category: "email_history",
    operators: ["is_true", "is_false"],
  },
  {
    id: "last_email_sent",
    label: "Last Email Sent",
    type: "date",
    category: "email_history",
    operators: ["is", "is_before", "is_after", "is_more_than_days_ago"],
  },
];

// ============================================================================
// Category Definitions
// ============================================================================

export const FILTER_CATEGORIES: FilterCategoryDefinition[] = [
  {
    id: "contact",
    label: "Contact Properties",
    icon: "👤",
    description: "Employee demographics, roles, and account info",
    fields: contactFields,
  },
  {
    id: "company",
    label: "Company Properties",
    icon: "🏢",
    description: "Company details, location, and trade",
    fields: companyFields,
  },
  {
    id: "subscription",
    label: "Subscription & Access",
    icon: "🔐",
    description: "Eagles, LATA, and application access",
    fields: subscriptionFields,
  },
  {
    id: "event",
    label: "Event Engagement",
    icon: "🎫",
    description: "Event attendance and registration",
    fields: eventFields,
  },
  {
    id: "learning",
    label: "Learning Activity",
    icon: "📚",
    description: "Courses, badges, and learning paths",
    fields: learningFields,
  },
  {
    id: "performance",
    label: "Call Intelligence",
    icon: "📞",
    description: "Call scores and coaching needs",
    fields: performanceFields,
  },
  {
    id: "financial",
    label: "Financial",
    icon: "💰",
    description: "Spend, invoices, and payments",
    fields: financialFields,
  },
  {
    id: "email_history",
    label: "Email History",
    icon: "📧",
    description: "Previous campaigns and engagement",
    fields: emailHistoryFields,
  },
];

// ============================================================================
// Helper Functions
// ============================================================================

export function getFieldById(fieldId: string): FilterFieldDefinition | undefined {
  for (const category of FILTER_CATEGORIES) {
    const field = category.fields.find((f) => f.id === fieldId);
    if (field) return field;
  }
  return undefined;
}

export function getCategoryById(categoryId: string): FilterCategoryDefinition | undefined {
  return FILTER_CATEGORIES.find((c) => c.id === categoryId);
}

export function getAllFields(): FilterFieldDefinition[] {
  return FILTER_CATEGORIES.flatMap((c) => c.fields);
}

export function getOperatorsForFieldType(type: FieldType): FilterOperator[] {
  switch (type) {
    case "text":
      return ["equals", "not_equals", "contains", "not_contains", "starts_with", "ends_with", "is_known", "is_unknown"];
    case "number":
      return ["equals", "not_equals", "greater_than", "greater_than_or_equal", "less_than", "less_than_or_equal", "between"];
    case "date":
      return ["is", "is_before", "is_after", "is_between", "is_more_than_days_ago", "is_less_than_days_ago", "is_known", "is_unknown"];
    case "select":
    case "multi_select":
      return ["is_any_of", "is_none_of", "is_known", "is_unknown"];
    case "boolean":
      return ["is_true", "is_false"];
    default:
      return [];
  }
}
